import { Outside, useDeviceType } from '@cian/ui-kit';
import { DropdownPopover } from '@cian/ui-kit/popover';
import { SelectMobileModal } from '@cian/ui-kit/select/components/SelectMobile/SelectMobileModal';
import { UIText1, UIText2 } from '@cian/ui-kit/typography';
import { IconActionSort16, IconFunctionalSuccess16 } from '@cian/ui-kit-design-tokens/icons';
import cn from 'classnames';
import * as React from 'react';

import * as styles from './SortDropdown.css';
import { ISortDropdownProps } from './types';
import { TSortKey } from '../../utils/sort/sortMapping';

export const SortDropdown: React.FC<ISortDropdownProps> = ({ options, value, className, onChange }) => {
  const deviceType = useDeviceType();
  const isPhone = deviceType === 'phone';
  const LabelText = isPhone ? UIText2 : UIText1;

  const [isOpen, setIsOpen] = React.useState(false);

  const activeOption = React.useMemo(
    () => options.find(option => option.key === value) ?? options[0],
    [options, value],
  );
  const activeLabel = activeOption?.label ?? '';

  const handleOpen = React.useCallback(() => setIsOpen(true), []);
  const handleClose = React.useCallback(() => setIsOpen(false), []);

  const handleSelect = React.useCallback(
    (nextKey: TSortKey) => {
      setIsOpen(false);
      if (nextKey !== value) {
        onChange(nextKey);
      }
    },
    [onChange, value],
  );

  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const button = (
    <button
      ref={buttonRef}
      type="button"
      className={styles['button']}
      onClick={isOpen ? handleClose : handleOpen}
      data-testid="SortDropdownButton"
    >
      <span className={styles['label']}>
        <LabelText color="primary_100">{activeLabel}</LabelText>
      </span>
      <span className={styles['icon']}>
        <IconActionSort16 color="icon-main-default" />
      </span>
    </button>
  );

  if (isPhone) {
    const mobileOptions = options.map(option => ({ label: option.label, value: option.key }));

    return (
      <div className={cn(styles['root'], className)}>
        {button}
        <SelectMobileModal
          open={isOpen}
          onClose={handleClose}
          options={mobileOptions}
          value={value}
          onChange={nextValue => handleSelect(nextValue as TSortKey)}
          title="Сортировка"
          dataTestId="SortDropdownMobileModal"
        />
      </div>
    );
  }

  const menu = (
    <Outside active={isOpen} onOutside={handleClose} insideRefs={[buttonRef, menuRef]}>
      <div ref={menuRef} className={styles['menu']} data-testid="SortDropdownMenu">
        {options.map(option => (
          <button
            key={option.key}
            type="button"
            className={cn(styles['menu-item'], option.key === value && styles['menu-item-active'])}
            onClick={() => handleSelect(option.key)}
            data-testid={`SortDropdownMenuItem-${option.key}`}
          >
            <UIText1>{option.label}</UIText1>
            {option.key === value && (
              <span className={styles['menu-item-icon']}>
                <IconFunctionalSuccess16 color="icon-main-default" />
              </span>
            )}
          </button>
        ))}
      </div>
    </Outside>
  );

  return (
    <div className={cn(styles['root'], className)}>
      <DropdownPopover open={isOpen} content={menu} sameWidth={false}>
        {button}
      </DropdownPopover>
    </div>
  );
};

SortDropdown.displayName = 'SortDropdown';
